import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Brain, Sparkles, Loader2, RefreshCw, AlertTriangle, ShieldCheck,
    ShieldAlert, Activity, ArrowRight,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import AIBadge from '../ai/AIBadge';
import api from '../../lib/api';

/**
 * AICoachingCard — "Que ferait un top performer ?" sur une fiche mandat (Sprint 5)
 *
 * GET /api/ai/coaching/mandate/{mandate_id}
 *  → risk_score déterministe (0-100) toujours affiché
 *  → bouton "Demander un coaching IA" qui appelle Claude Sonnet 4.5 (1 quota décompté)
 *  → 3 actions priorisées avec impact € + diagnostic en 3 points
 *
 * Le risk_score s'affiche AUTO au mount (gratuit, 0 quota), Claude n'est appelé qu'au clic.
 */
const RISK_PALETTE = {
    Sain: { from: '#10B981', to: '#34D399', icon: ShieldCheck, sub: 'Tout est sous contrôle' },
    'À surveiller': { from: '#F59E0B', to: '#FBBF24', icon: Activity, sub: 'Quelques signaux à surveiller' },
    'À risque': { from: '#F97316', to: '#FB923C', icon: ShieldAlert, sub: 'Le mandat dérive — action requise' },
    Critique: { from: '#EF4444', to: '#F87171', icon: AlertTriangle, sub: 'Mandat en perte — agir cette semaine' },
};

function fmtEUR(n) {
    if (n === null || n === undefined || n === 0) return null;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

export default function AICoachingCard({ mandateId }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [hasGenerated, setHasGenerated] = useState(false);

    // Initial fetch on cached entry (only risk_score visible, no LLM call backend-side if already cached)
    const fetchCoaching = useCallback(async (force = false) => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get(`/ai/coaching/mandate/${mandateId}`, {
                params: force ? { _ts: Date.now() } : {},
            });
            setData(res?.data || null);
            setHasGenerated(true);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || e?.message || 'Erreur inconnue';
            if (status === 403) setError('Accès refusé · ce mandat est hors de votre périmètre.');
            else setError(detail);
        } finally {
            setLoading(false);
        }
    }, [mandateId]);

    // Pre-warm: try to read cache only (no harm if backend re-uses cache). The user still sees the explicit "Generate" CTA the first time.
    useEffect(() => {
        // Don't auto-fetch on mount to avoid burning the quota on simple navigations.
        // We only display the risk meter empty state until user clicks.
    }, [mandateId]);

    const risk = data?.risk_score ?? null;
    const riskLabel = data?.risk_label || (risk === null ? 'Non analysé' : 'Sain');
    const palette = RISK_PALETTE[riskLabel] || RISK_PALETTE.Sain;
    const RiskIcon = palette.icon;

    return (
        <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl"
            style={{ fontFamily: 'IBM Plex Sans, Manrope, sans-serif' }}
            data-testid="ai-coaching-card"
        >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 p-5 border-b border-white/5">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] via-[#A855F7] to-[#FF914D] flex items-center justify-center shrink-0">
                        <Brain className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0">
                        <p
                            className="text-[10px] uppercase tracking-widest text-white/40"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        >
                            Coaching IA
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3
                                className="text-lg font-semibold text-white tracking-tight"
                                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                            >
                                Que ferait un top performer ?
                            </h3>
                            <AIBadge persona="coach" size="sm" />
                        </div>
                    </div>
                </div>
                {data && (
                    <button
                        onClick={() => fetchCoaching(true)}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-xs font-semibold text-white/70 hover:text-white transition-all disabled:opacity-50"
                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        data-testid="ai-coaching-refresh"
                    >
                        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                        Régénérer
                    </button>
                )}
            </div>

            {/* Body */}
            <div className="p-5 space-y-5">
                {/* Risk meter (always when data present) */}
                {data && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
                        <div
                            className="md:col-span-1 rounded-xl border p-4"
                            style={{
                                background: `linear-gradient(135deg, ${palette.from}14, ${palette.to}0a)`,
                                borderColor: `${palette.from}44`,
                            }}
                            data-testid="ai-coaching-risk-meter"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <RiskIcon className="w-4 h-4" style={{ color: palette.from }} />
                                <span
                                    className="text-[10px] uppercase tracking-widest"
                                    style={{ fontFamily: 'Space Grotesk, sans-serif', color: palette.from }}
                                >
                                    Risk score
                                </span>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span
                                    className="text-3xl font-bold text-white"
                                    style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                                    data-testid="ai-coaching-risk-score"
                                >
                                    {data.risk_score}
                                </span>
                                <span className="text-sm text-white/40" style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
                                    / 100
                                </span>
                            </div>
                            <p
                                className="mt-2 text-sm font-semibold"
                                style={{ fontFamily: 'Space Grotesk, sans-serif', color: palette.from }}
                            >
                                {data.risk_label}
                            </p>
                            <p className="mt-1 text-[11px] text-white/50">{palette.sub}</p>
                            {/* Risk progress bar */}
                            <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                <motion.div
                                    className="h-full"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${data.risk_score}%` }}
                                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                                    style={{ background: `linear-gradient(90deg, ${palette.from}, ${palette.to})` }}
                                />
                            </div>
                        </div>

                        {/* Risk reasons */}
                        <div
                            className="md:col-span-2 rounded-xl border border-white/10 bg-black/20 p-4"
                            data-testid="ai-coaching-risk-reasons"
                        >
                            <p
                                className="text-[10px] uppercase tracking-widest text-white/40 mb-2"
                                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                            >
                                Raisons du score
                            </p>
                            {(data.risk_reasons || []).length === 0 ? (
                                <p className="text-sm text-emerald-300">
                                    Aucun signal d'alerte détecté. Le rythme actuel est bon.
                                </p>
                            ) : (
                                <ul className="space-y-1.5">
                                    {data.risk_reasons.map((r, i) => (
                                        <li key={`r-${i}`} className="text-sm text-white/85 flex items-start gap-2">
                                            <span style={{ color: palette.from }}>•</span>
                                            <span>{r}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {/* Empty state — CTA "Generate" */}
                {!data && !loading && !error && (
                    <div className="text-center py-6" data-testid="ai-coaching-empty">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-[#681793]/30 to-[#FF914D]/30 mb-3">
                            <Sparkles className="w-5 h-5 text-[#C084FC]" />
                        </div>
                        <p
                            className="text-sm font-semibold text-white mb-1"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        >
                            Coaching à la demande
                        </p>
                        <p className="text-xs text-white/50 mb-4 max-w-md mx-auto">
                            L'IA analyse l'historique de ce mandat et vous propose ce qu'un top performer ferait cette semaine.
                            <span className="text-white/40"> 1 question décomptée du quota agence.</span>
                        </p>
                        <button
                            onClick={() => fetchCoaching(false)}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#681793] to-[#A855F7] hover:opacity-90 text-white text-sm font-semibold shadow-lg shadow-[#A855F7]/20 transition-all"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                            data-testid="ai-coaching-generate"
                        >
                            <Brain className="w-4 h-4" />
                            Demander un coaching IA
                        </button>
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="text-center py-8" data-testid="ai-coaching-loading">
                        <Loader2 className="w-5 h-5 text-[#A855F7] animate-spin mx-auto mb-2" />
                        <p className="text-sm text-white/60" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            L'IA analyse ce mandat…
                        </p>
                        <p className="text-[11px] text-white/30 mt-1">~ 5 à 10 secondes</p>
                    </div>
                )}

                {/* Error */}
                <AnimatePresence>
                    {error && !loading && (
                        <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5 flex items-start gap-3"
                            data-testid="ai-coaching-error"
                        >
                            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm text-amber-200" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                                    {error}
                                </p>
                            </div>
                            <button
                                onClick={() => fetchCoaching(true)}
                                className="text-amber-300 hover:text-amber-100 transition-colors"
                                data-testid="ai-coaching-retry"
                            >
                                <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Diagnostic + Actions */}
                {data && (
                    <>
                        <div data-testid="ai-coaching-diagnostic">
                            <p
                                className="text-[10px] uppercase tracking-widest text-white/40 mb-2"
                                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                            >
                                Diagnostic
                            </p>
                            <ul className="space-y-1.5">
                                {(data.diagnostic || []).map((d, i) => (
                                    <li
                                        key={`d-${i}`}
                                        className="text-sm text-white/85 leading-relaxed flex items-start gap-2"
                                        data-testid={`ai-coaching-diagnostic-${i}`}
                                    >
                                        <span className="text-[#FF914D] mt-1">▸</span>
                                        <span>{d}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div data-testid="ai-coaching-actions">
                            <p
                                className="text-[10px] uppercase tracking-widest text-white/40 mb-2"
                                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                            >
                                Ce que ferait un top performer
                            </p>
                            <div className="space-y-2">
                                {(data.top_performer_actions || []).map((a, i) => (
                                    <ActionRow key={`a-${i}`} action={a} index={i} />
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between gap-2 text-[11px] text-white/40">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-[#FF914D]" />
                    <span style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {data?.model_used === 'cached'
                            ? "Réponse partagée avec l'équipe (cache 1 h)"
                            : data?.model_used?.startsWith('claude')
                                ? 'Propulsé par Coach · IA Homly'
                                : data?.model_used === 'fallback-rules-quota-exceeded'
                                    ? 'Quota IA atteint · réponse basée sur des règles métier'
                                    : 'Powered by Homly AI'}
                    </span>
                </div>
                {data && (
                    <span data-testid="ai-coaching-quota" style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
                        Quota agence : {data.quota_used} / {data.quota_total}
                    </span>
                )}
            </div>
        </motion.section>
    );
}

/* ──────────── Sub-components ──────────── */
function ActionRow({ action, index }) {
    const Icon = LucideIcons[action.icon] || LucideIcons.Target;
    const eur = fmtEUR(action.impact_eur);
    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="group flex items-start gap-3 p-3.5 rounded-xl bg-white/[0.02] border border-white/8 hover:border-white/20 hover:bg-white/[0.04] transition-all"
            style={{ borderLeft: '3px solid #A855F7' }}
            data-testid={`ai-coaching-action-${index}`}
        >
            <div
                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: '#A855F71a', border: '1px solid #A855F733' }}
            >
                <Icon className="w-4 h-4 text-[#C084FC]" />
            </div>
            <div className="flex-1 min-w-0">
                <p
                    className="text-sm font-semibold text-white leading-snug"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                    {action.label}
                </p>
                {action.why && (
                    <p className="text-xs text-white/55 leading-relaxed mt-1">
                        {action.why}
                    </p>
                )}
                {eur && (
                    <p className="text-[11px] text-white/70 mt-1.5 flex items-center gap-1.5">
                        <span style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }} className="text-[#FF914D] font-semibold">
                            {eur}
                        </span>
                        <span className="text-white/40">·</span>
                        <span className="uppercase tracking-wider text-[9px] text-white/40" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            {action.impact_label || 'impact'}
                        </span>
                    </p>
                )}
            </div>
            <ArrowRight className="w-4 h-4 text-white/20 shrink-0 mt-1" />
        </motion.div>
    );
}
